import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Loader2,
  ShieldAlert,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import {
  getIsAdmin,
  listReports,
  resolveReport,
} from "@/lib/reports.functions";

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error("未ログインです");
  return { Authorization: `Bearer ${data.session.access_token}` };
}

export const Route = createFileRoute("/_authenticated/admin_/reports")({
  head: () => ({
    meta: [
      { title: "通報管理 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminReportsPage,
});

const REASON_LABEL: Record<string, string> = {
  adult: "成人向け",
  violence: "暴力",
  impersonation: "なりすまし",
  copyright: "著作権",
  minor: "未成年",
  hate: "ヘイト",
  other: "その他",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "未対応",
  approved: "承認(削除)",
  rejected: "却下",
  escalated: "エスカレ",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "destructive",
  approved: "default",
  rejected: "secondary",
  escalated: "outline",
};

type Report = {
  id: string;
  reporter_id: string | null;
  reporter_email: string | null;
  reported_generation_id: string;
  reasons: string[];
  detail: string | null;
  status: string;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
};

type GenInfo = {
  id: string;
  user_id: string;
  output_video_url: string | null;
  is_blocked: boolean;
  deleted_at: string | null;
  created_at: string;
};

function AdminReportsPage() {
  const navigate = useNavigate();
  const isAdminFn = useServerFn(getIsAdmin);
  const listFn = useServerFn(listReports);
  const resolveFn = useServerFn(resolveReport);

  const [authzChecked, setAuthzChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [reasonFilter, setReasonFilter] = useState<string>("all");
  const [reports, setReports] = useState<Report[]>([]);
  const [gens, setGens] = useState<Record<string, GenInfo>>({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders();
        const { isAdmin } = await isAdminFn({ headers });
        setIsAdmin(isAdmin);
      } catch {
        setIsAdmin(false);
      } finally {
        setAuthzChecked(true);
      }
    })();
  }, [isAdminFn]);

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await listFn({
        headers,
        data: {
          status: statusFilter as "all" | "pending" | "approved" | "rejected" | "escalated",
          reason: reasonFilter !== "all" ? reasonFilter : undefined,
        },
      });
      setReports(res.reports as Report[]);
      setGens(res.generationsById as Record<string, GenInfo>);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authzChecked && isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authzChecked, isAdmin, statusFilter, reasonFilter]);

  const onAction = async (
    reportId: string,
    action: "approve" | "reject" | "escalate",
  ) => {
    setActing(true);
    try {
      const headers = await authHeaders();
      await resolveFn({ headers, data: { reportId, action, note: actionNote || undefined } });
      toast.success("対応を記録しました");
      setActionNote("");
      setExpandedId(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "操作に失敗しました");
    } finally {
      setActing(false);
    }
  };

  if (!authzChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">アクセス権がありません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              この画面は管理者ロールを持つユーザーのみが利用できます。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">通報の管理</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              ユーザーから提出された通報を確認し、対応してください。
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            再読み込み
          </Button>
        </div>

        <Card className="mt-6 p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs">ステータス</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">未対応</SelectItem>
                  <SelectItem value="all">すべて</SelectItem>
                  <SelectItem value="approved">承認済</SelectItem>
                  <SelectItem value="rejected">却下</SelectItem>
                  <SelectItem value="escalated">エスカレ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">理由</Label>
              <Select value={reasonFilter} onValueChange={setReasonFilter}>
                <SelectTrigger className="mt-1 w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  {Object.entries(REASON_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="mt-12 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reports.length === 0 ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">
            該当する通報はありません。
          </p>
        ) : (
          <ul className="mt-6 space-y-3">
            {reports.map((r) => {
              const gen = gens[r.reported_generation_id];
              const expanded = expandedId === r.id;
              return (
                <Card key={r.id} className="p-4">
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedId(expanded ? null : r.id);
                      setActionNote(r.resolution_note ?? "");
                    }}
                    className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge variant={STATUS_VARIANT[r.status] ?? "outline"}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("ja-JP")}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {r.reasons.map((rsn) => (
                          <Badge key={rsn} variant="secondary">
                            {REASON_LABEL[rsn] ?? rsn}
                          </Badge>
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        通報者: {r.reporter_email ?? "匿名"}
                      </span>
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`}
                    />
                  </button>

                  {expanded && (
                    <div className="mt-4 space-y-4 border-t pt-4">
                      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
                        <div>
                          {gen?.output_video_url ? (
                            <video
                              src={gen.output_video_url}
                              controls
                              className="w-full rounded-lg bg-black"
                            />
                          ) : (
                            <div className="flex aspect-video items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
                              動画なし
                            </div>
                          )}
                          <div className="mt-2 text-xs text-muted-foreground">
                            <p>動画ID: <span className="font-mono">{r.reported_generation_id.slice(0, 8)}…</span></p>
                            {gen && (
                              <>
                                <p>状態: {gen.is_blocked ? "🚫 ブロック中" : "公開中"}</p>
                                {gen.deleted_at && <p>削除済 ({new Date(gen.deleted_at).toLocaleString("ja-JP")})</p>}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="space-y-3 text-sm">
                          {r.detail && (
                            <div>
                              <Label className="text-xs">詳細</Label>
                              <p className="mt-1 whitespace-pre-wrap rounded-md border bg-muted/40 p-3">
                                {r.detail}
                              </p>
                            </div>
                          )}
                          <div>
                            <Label className="text-xs">対応メモ</Label>
                            <Textarea
                              value={actionNote}
                              onChange={(e) => setActionNote(e.target.value)}
                              placeholder="対応の判断理由など"
                              className="mt-1 min-h-[80px]"
                            />
                          </div>

                          {r.status === "pending" || r.status === "escalated" ? (
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={acting}
                                onClick={() => onAction(r.id, "approve")}
                              >
                                <AlertTriangle className="mr-1 h-4 w-4" />
                                承認(動画削除)
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={acting}
                                onClick={() => onAction(r.id, "reject")}
                              >
                                <X className="mr-1 h-4 w-4" />
                                却下
                              </Button>
                              {r.status !== "escalated" && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={acting}
                                  onClick={() => onAction(r.id, "escalate")}
                                >
                                  エスカレーション
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center text-xs text-muted-foreground">
                              <Check className="mr-1 h-3 w-3" />
                              対応済
                              {r.resolved_at && (
                                <span className="ml-2">
                                  ({new Date(r.resolved_at).toLocaleString("ja-JP")})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}
